<template>
  <section class="doc-band">
    <div class="container">
      <!-- 面包屑：首页 / 数据产品 / 分类 / 接口名 -->
      <nav class="crumb">
        <router-link to="/">首页</router-link>
        <span class="crumb-sep">/</span>
        <router-link to="/api">数据产品</router-link>
        <span class="crumb-sep">/</span>
        <router-link :to="catHref">{{ catName || '接口目录' }}</router-link>
        <span class="crumb-sep">/</span>
        <span class="crumb-cur">{{ detailObj.name || '接口详情' }}</span>
      </nav>

      <div class="doc-grid">
        <!-- 左侧接口列表：复用 ApiList.vue，数据来自 portal/api/dim/interface/detail -->
        <aside class="doc-side">
          <ApiList
            :dims="dimList"
            :total="total"
            :active-id="selectId"
            :loading="leftLoading"
            @select="handleSelect"
          />
        </aside>

        <div class="doc-main">
          <!-- 头部：接口名 + 分类 / ID / 请求方式 / 返回格式 / 价格 + 申请接口 / 立即测试 -->
          <div class="doc-head">
            <div class="doc-title">
              <h1>{{ detailObj.name || '接口详情' }}</h1>
              <div class="doc-tags">
                <span class="doc-tag cat" v-if="catName">{{ catName }}</span>
                <span class="doc-tag" v-if="detailObj.showId">接口 ID {{ detailObj.showId }}</span>
                <span class="badge get" v-if="reqMethod">{{ reqMethod }}</span>
                <span class="badge json" v-if="respFormat">{{ respFormat }}</span>
                <span class="doc-tag price" v-if="detailObj.price">￥{{ detailObj.price }}</span>
              </div>
              <p class="doc-summary">{{ detailObj.descr || '—' }}</p>
            </div>
            <div class="doc-actions">
              <button class="btn btn-ghost btn-sm" type="button" @click="apply">申请接口</button>
              <button class="btn btn-primary btn-sm" type="button" @click="goTest">立即测试</button>
            </div>
          </div>

          <!-- 6 个页签 -->
          <div class="doc-tabs" role="tablist">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              class="doc-tab"
              type="button"
              role="tab"
              :class="{ active: activeTab === tab.key }"
              :aria-selected="activeTab === tab.key ? 'true' : 'false'"
              @click="activeTab = tab.key"
            >{{ tab.label }}</button>
          </div>

          <!-- ① 接口说明：身份与摘要公开；示例代码属完整文档，登录后可见 -->
          <div class="doc-card" data-tab="info" v-show="activeTab === 'info'">
            <h3 class="doc-sec-title">基本信息</h3>
            <table class="doc-table kv">
              <tbody>
                <tr><th scope="row">接口地址</th><td class="mono">{{ detailObj.domainUrl || '—' }}</td></tr>
                <tr><th scope="row">请求方式</th><td>{{ reqMethod || '—' }}</td></tr>
                <tr><th scope="row">返回参数格式</th><td>{{ respFormat || '—' }}</td></tr>
                <tr><th scope="row">请求参数</th><td class="mono">{{ detailObj.reqParam || '—' }}</td></tr>
                <!-- 后端 price 字段自带单位（如 0.20/次），不做二次拼接 -->
                <tr><th scope="row">价格</th><td>{{ detailObj.price ? '￥' + detailObj.price : '—' }}</td></tr>
              </tbody>
            </table>

            <h3 class="doc-sec-title">请求示例</h3>
            <pre class="api-doc">{{ detailObj.reqExample || '—' }}</pre>

            <h3 class="doc-sec-title">授权认证</h3>
            <table class="doc-table kv">
              <tbody>
                <tr><th scope="row">认证方式</th><td>{{ detailObj.authorizationWay || '—' }}</td></tr>
                <tr><th scope="row">认证示例</th><td class="mono">{{ detailObj.authorizationExample || '—' }}</td></tr>
              </tbody>
            </table>

            <!-- 示例代码（完整文档，需登录） -->
            <div class="gate doc-gate" :class="{ gated: !docUnlocked }">
              <div class="gate-inner">
                <h3 class="doc-sec-title">示例代码</h3>
                <div class="lang-chips">
                  <button
                    v-for="lang in langs"
                    :key="lang.id"
                    class="lang-chip"
                    type="button"
                    :class="{ active: langIndex === lang.id }"
                    @click="changeTab(lang.id)"
                  >{{ lang.label }}</button>
                </div>

                <pre class="api-doc" v-if="langIndex === 1">
package com.example.demo.http;

import org.apache.http.client.methods.CloseableHttpResponse;
import org.apache.http.client.methods.HttpGet;
import org.apache.http.impl.client.CloseableHttpClient;
import org.apache.http.impl.client.HttpClients;
import org.apache.http.util.EntityUtils;
import java.io.IOException;

public class HttpDemo {

    public static void main(String[] args) {
        String secretkey = "您的secretkey";
        //请求示例地址
        String url = "请求的接口地址";

        HttpGet httpGet = new HttpGet(url);
        httpGet.setHeader("secretkey", secretkey);

        CloseableHttpClient httpClient = HttpClients.createDefault();
        CloseableHttpResponse httpResponse;
        String result = null;
        try {
            httpResponse = httpClient.execute(httpGet);
            if (httpResponse.getStatusLine().getStatusCode() == 200) {
                result = EntityUtils.toString(httpResponse.getEntity(), "utf-8");
            }
        } catch (IOException e) {
            e.printStackTrace();
        } finally {
            try {
                httpClient.close();
            } catch (IOException e) {
                e.printStackTrace();
            }
        }
        System.out.println(result);
    }
}
                </pre>

                <pre class="api-doc" v-if="langIndex === 2">
&lt;?php

$secretkey = "您的secretkey";
//请求示例地址
$url="请求的接口地址";

$curl = curl_init();
$header = ["secretkey: $secretkey"];

curl_setopt($curl, CURLOPT_URL, $url);
curl_setopt($curl, CURLOPT_HEADER, 0);
curl_setopt($curl, CURLOPT_RETURNTRANSFER, 1);
curl_setopt($curl, CURLOPT_HTTPHEADER, $header);

$data = curl_exec($curl);
curl_close($curl);

// 打印结果
print_r($data);

?&gt;
                </pre>

                <pre class="api-doc" v-if="langIndex === 3">
package main

import (
    "fmt"
    "io/ioutil"
    "net/http"
    "strings"
)

func main() {

    secretkey := "您的secretkey";
    //请求示例地址
    url := "请求的接口地址";

    client := &amp;http.Client{}
    req,_ := http.NewRequest("GET",url,nil)
    req.Header.Set("secretkey",secretkey)

    resp,_ := client.Do(req)
    body, _ := ioutil.ReadAll(resp.Body)
    fmt.Printf(string(body))
}
                </pre>

                <pre class="api-doc" v-if="langIndex === 4">
# -*- coding: utf-8 -*-
# pip install request
import requests

secretkey = "您的secretkey"
# 请求示例地址
url = "请求的接口地址"

headers = {"secretkey": secretkey}
try:
    response = requests.get(url, headers=headers)
    print(response)
except Exception as e:
    print(e)
                </pre>

                <pre class="api-doc" v-if="langIndex === 5">
var secretkey="secretkey";
//请求示例地址
var url="请求的接口地址"
var http = require("http");

var options = {
    hostname:'api.workatdata.com',
    method:'GET',
    path:url,
    headers:{
        "secretkey": secretkey
    }
}

var req = http.request(options, function (res) {
    res.on('data', function (chunk) {
        console.log('result: ' + chunk);
    });
});

req.on('error', function (e) {
    console.log('problem with request: ' + e.message);
});

req.end();
                </pre>
              </div>
              <div class="gate-mask">
                <div class="gate-mask-inner">
                  <span class="gate-icon" v-html="lockIcon"></span>
                  <p class="gate-title">示例代码为登录用户专享</p>
                  <p class="gate-sub">登录后可查看各语言接入示例</p>
                  <div class="gate-btns">
                    <button class="btn btn-primary btn-sm" type="button" @click="openAuth('login')">立即登录</button>
                    <button class="btn btn-ghost btn-sm" type="button" @click="openAuth('register')">免费注册</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ② 请求参数 -->
          <div class="gate doc-gate" data-tab="params" :class="{ gated: !docUnlocked }" v-show="activeTab === 'params'">
            <div class="gate-inner">
              <div class="doc-card">
                <h3 class="doc-sec-title">请求参数（Query）</h3>
                <table class="doc-table" v-if="tableData && tableData.length">
                  <thead>
                    <tr><th>参数名</th><th>类型</th><th>是否必填</th><th>说明</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in tableData" :key="item.name">
                      <td class="mono">{{ item.name }}</td>
                      <td>{{ item.type || '—' }}</td>
                      <td :class="{ req: isRequired(item) }">{{ item.fill || '—' }}</td>
                      <td>{{ item.desc || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
                <p class="doc-p" v-else>该接口无请求参数</p>
              </div>
            </div>
            <div class="gate-mask">
              <div class="gate-mask-inner">
                <span class="gate-icon" v-html="lockIcon"></span>
                <p class="gate-title">请求参数为登录用户专享</p>
                <p class="gate-sub">登录后可查看完整参数表</p>
                <div class="gate-btns">
                  <button class="btn btn-primary btn-sm" type="button" @click="openAuth('login')">立即登录</button>
                  <button class="btn btn-ghost btn-sm" type="button" @click="openAuth('register')">免费注册</button>
                </div>
              </div>
            </div>
          </div>

          <!-- ③ 返回字段 -->
          <div class="gate doc-gate" data-tab="resp" :class="{ gated: !docUnlocked }" v-show="activeTab === 'resp'">
            <div class="gate-inner">
              <div class="doc-card">
                <h3 class="doc-sec-title">返回字段说明</h3>
                <table class="doc-table" v-if="fieldRows.length">
                  <thead>
                    <tr><th>参数名</th><th>字段类型</th><th>长度</th><th>备注</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="field in fieldRows" :key="field.uniqueKey">
                      <td class="mono">
                        <span :style="{ paddingLeft: (field.depth * 18) + 'px' }">{{ field.name }}</span>
                      </td>
                      <td>{{ field.type || '—' }}</td>
                      <td>{{ field.length || '—' }}</td>
                      <td>{{ field.desc || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
                <p class="doc-p" v-else>暂无返回字段说明</p>
              </div>
            </div>
            <div class="gate-mask">
              <div class="gate-mask-inner">
                <span class="gate-icon" v-html="lockIcon"></span>
                <p class="gate-title">返回字段为登录用户专享</p>
                <p class="gate-sub">登录后可查看完整字段说明</p>
                <div class="gate-btns">
                  <button class="btn btn-primary btn-sm" type="button" @click="openAuth('login')">立即登录</button>
                  <button class="btn btn-ghost btn-sm" type="button" @click="openAuth('register')">免费注册</button>
                </div>
              </div>
            </div>
          </div>

          <!-- ④ 错误代码 -->
          <div class="gate doc-gate" data-tab="errors" :class="{ gated: !docUnlocked }" v-show="activeTab === 'errors'">
            <div class="gate-inner">
              <div class="doc-card">
                <h3 class="doc-sec-title">错误代码</h3>
                <table class="doc-table" v-if="codeData && codeData.length">
                  <thead>
                    <tr><th>代码</th><th>说明</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in codeData" :key="item.code">
                      <td class="mono">{{ item.code }}</td>
                      <td>{{ item.typeDesc }}</td>
                    </tr>
                  </tbody>
                </table>
                <p class="doc-p" v-else>暂无错误代码说明</p>
              </div>
            </div>
            <div class="gate-mask">
              <div class="gate-mask-inner">
                <span class="gate-icon" v-html="lockIcon"></span>
                <p class="gate-title">错误代码为登录用户专享</p>
                <p class="gate-sub">登录后可查看完整错误码表</p>
                <div class="gate-btns">
                  <button class="btn btn-primary btn-sm" type="button" @click="openAuth('login')">立即登录</button>
                  <button class="btn btn-ghost btn-sm" type="button" @click="openAuth('register')">免费注册</button>
                </div>
              </div>
            </div>
          </div>

          <!-- ⑤ 返回示例：未登录只展示前 3 行 + 登录提示 -->
          <div class="doc-card" data-tab="example" v-show="activeTab === 'example'">
            <h3 class="doc-sec-title">返回结果示例</h3>
            <pre class="api-doc">{{ exampleText }}</pre>
            <div class="doc-partial-tip" v-if="!docUnlocked">
              <span v-html="lockIcon"></span>
              <span>未登录仅展示前 3 行返回示例，登录后可查看完整内容</span>
              <button class="btn btn-primary btn-sm" type="button" @click="openAuth('login')">立即登录</button>
            </div>
          </div>

          <!-- ⑥ 在线测试：挂 ApiPanel.vue；首次打开才挂载，未登录沿用面板自带门禁提示 -->
          <div class="doc-card doc-card-test" data-tab="test" v-show="activeTab === 'test'">
            <ApiPanel
              v-if="testOpened && panelApi"
              :api="panelApi"
              :api-detail="detailObj"
              :logged="logged"
              :doc-link="false"
            />
            <p class="doc-more-fields">
              也可以
              <a href="javascript:;" @click="testApi">前往在线接口测试页</a>
              （携带当前分类与接口参数）。
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ApiList from '@/components/ApiList';
import ApiPanel from '@/components/ApiPanel';
import { portalApi } from '@/api';

/* 整体上锁开关：
   false（默认）= 分级门禁 —— 身份与摘要（头部 + 接口说明的基本信息）公开，
                   完整文档（示例代码 / 请求参数 / 返回字段 / 错误代码 / 返回示例全文 / 在线测试）需登录；
   true          = 整体上锁 —— 连摘要也一并模糊（等价 handoff 的 body.gated 全站开关）。 */
const DOC_LOCK_ALL = false;

const LOCK_ICON =
  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<rect x="3" y="11" width="18" height="11" rx="2"></rect>' +
  '<path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>';

/* 返回字段树形展示：把 respFiledDesc 的 children 摊平成带 depth 的行（替代旧 el-table 的树形展开） */
function flattenFields(list, depth, out) {
  (list || []).forEach(function (item) {
    out.push(Object.assign({}, item, { depth: depth }));
    if (item.children && item.children.length) {
      flattenFields(item.children, depth + 1, out);
    }
  });
  return out;
}

/* 返回字段：按字段名递归挂 children，并生成唯一 key 供表格渲染（沿用旧版实现） */
const fillChildren = (item) => {
  const itemName = item['name'];
  item['uniqueKey'] = generateUuid(16, 16);
  if (item[itemName] && item[itemName] instanceof Array) {
    item['children'] = item[itemName];
    item['children'].forEach((childranItem) => {
      fillChildren(childranItem);
    });
  }
};

const generateUuid = (len, radix) => {
  var chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('');
  var uuid = [], i;
  radix = radix || chars.length;

  if (len) {
    // Compact form
    for (i = 0; i < len; i++) uuid[i] = chars[0 | Math.random() * radix];
  } else {
    // rfc4122, version 4 form
    var r;

    // rfc4122 requires these characters
    uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-';
    uuid[14] = '4';

    // Fill in random data.  At i==19 set the high bits of clock sequence as
    // per rfc4122, sec. 4.1.5
    for (i = 0; i < 36; i++) {
      if (!uuid[i]) {
        r = 0 | Math.random() * 16;
        uuid[i] = chars[(i == 19) ? (r & 0x3) | 0x8 : r];
      }
    }
  }
  return uuid.join('');
};

export default {
  name: 'apiDetail',
  components: { ApiList, ApiPanel },
  data() {
    return {
      tabs: [
        { key: 'info', label: '接口说明' },
        { key: 'params', label: '请求参数' },
        { key: 'resp', label: '返回字段' },
        { key: 'errors', label: '错误代码' },
        { key: 'example', label: '返回示例' },
        { key: 'test', label: '在线测试' }
      ],
      langs: [
        { id: 1, label: 'JAVA' },
        { id: 2, label: 'PHP' },
        { id: 3, label: 'GO' },
        { id: 4, label: 'PYTHON' },
        { id: 5, label: 'Node.js' }
      ],
      activeTab: 'info',
      langIndex: 1,
      lockIcon: LOCK_ICON,
      shows: 0,
      selectId: 0,
      dimList: [],
      leftLoading: false,
      total: 0,
      detailObj: {},
      tableData: [],
      quaryData: [],
      codeData: [],
      respExample: undefined,
      testOpened: false
    };
  },
  computed: {
    logged() {
      const user = this.$cookies.getJSON('user');
      return !!(user && user.userId);
    },
    docUnlocked() {
      return DOC_LOCK_ALL ? false : this.logged;
    },
    reqMethod() {
      return this.detailObj.reqMethod ? String(this.detailObj.reqMethod).toUpperCase() : '';
    },
    respFormat() {
      return this.detailObj.respFormat ? String(this.detailObj.respFormat).toUpperCase() : '';
    },
    // 当前接口所属分类（优先按接口 ID 反查，回退到 URL / cookie 里的 dimId）
    cat() {
      const byId = this.dimList.filter(cat =>
        (cat.intfList || []).some(item => String(item.id) === String(this.selectId))
      )[0];
      if (byId) return byId;
      return this.dimList.filter(cat => String(cat.dimId) === String(this.shows))[0] || null;
    },
    catName() {
      return this.cat ? this.cat.dimName : '';
    },
    catHref() {
      return this.cat ? ('/api?dimId=' + this.cat.dimId) : '/api';
    },
    panelApi() {
      return {
        id: this.selectId,
        name: this.detailObj.name,
        dimId: this.cat ? this.cat.dimId : this.shows
      };
    },
    fieldRows() {
      return flattenFields(this.quaryData, 0, []);
    },
    exampleText() {
      if (!this.respExample) return '// 暂无返回示例';
      const full = JSON.stringify(this.respExample, null, 4);
      if (this.docUnlocked) return full;
      // 未登录：只给前 3 行
      return full.split('\n').slice(0, 3).join('\n') + '\n...';
    }
  },
  watch: {
    activeTab(val) {
      // 在线测试面板首次打开才挂载，避免页面加载时就发起测试相关请求
      if (val === 'test') this.testOpened = true;
    }
  },
  mounted() {
    this.shows = this.getUrlParams('dimId') || this.$cookies.get('dimId') || 0;
    this.selectId = this.getUrlParams('id') || this.$cookies.get('selectId') || 0;
    this.getLeft();
    this.getDetail(this.selectId);
  },
  beforeDestroy() {
    this.$cookies.remove('dimId');
    this.$cookies.remove('selectId');
  },
  methods: {
    changeTab(id) {
      this.langIndex = id;
    },
    isRequired(param) {
      return String((param && param.fill) || '').indexOf('是') > -1;
    },
    openAuth(view) {
      window.dispatchEvent(new CustomEvent('dts:auth-open', {
        detail: { view: view || 'login' }
      }));
    },
    // 申请接口：已登录跳个人中心「我的接口」，未登录唤起登录弹窗（行为与旧版一致）
    apply() {
      const user = this.$cookies.getJSON('user');
      if (user && user.userId) {
        this.$router.push('/center/case3?from=detail');
      } else {
        this.$store.commit('setShowLogin', 1);
      }
    },
    // 立即测试：未登录唤起登录弹窗，已登录切到「在线测试」页签
    goTest() {
      const user = this.$cookies.getJSON('user');
      if (!(user && user.userId)) {
        this.$store.commit('setShowLogin', 1);
        return;
      }
      this.activeTab = 'test';
    },
    // 保留旧路径入口：/apiTest?dimId=..&id=..（现由路由兼容跳转到数据产品页）
    testApi() {
      const user = this.$cookies.getJSON('user');
      if (user && user.userId) {
        this.$router.push(`/apiTest?dimId=${this.shows}&id=${this.selectId}`);
      } else {
        this.$store.commit('setShowLogin', 1);
      }
    },
    // 左侧列表选中：跳转 + 取详情 + 记忆（cookie 名称与写入时机与旧版一致）
    handleSelect(payload) {
      const api = payload && payload.api;
      const cat = payload && payload.cat;
      if (!api || !cat) return;
      this.$router.replace(`/apiDetail?dimId=${cat.dimId}&id=${api.id}`);
      this.shows = cat.dimId;
      this.getDetail(api.id);
      this.initPage();
      this.$cookies.set('dimId', cat.dimId, { expires: 1 });
      this.$cookies.set('selectId', api.id, { expires: 1 });
    },
    initPage() {
      window.scrollTo(0, 0);
      this.activeTab = 'info';
      this.langIndex = 1;
      this.testOpened = false;
    },
    getUrlParams(name) {
      const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i');
      // history 路由：参数在 location.search；同时兼容旧 hash 地址（#/apiDetail?dimId=..）
      const fromRoute = this.$route && this.$route.query ? this.$route.query[name] : undefined;
      if (fromRoute !== undefined && fromRoute !== null && fromRoute !== '') return fromRoute;
      const query =
        (window.location.search ? window.location.search.slice(1) : '') ||
        window.location.hash.split('?')[1] ||
        '';
      if (!query) return null;
      const r = query.match(reg);
      if (r != null) {
        return unescape(r[2]);
      }
      return null;
    },
    // 左侧分类 + 接口清单（portal/api/dim/interface/detail）
    getLeft() {
      this.leftLoading = true;
      portalApi.getInterfaceCatalog()
        .then((res) => {
          if (res.state === 200) {
            // 与原实现一致：状态码非 200 时提示错误
          } else {
            this.$message.error(res.message);
          }
          const data = res.data || {};
          this.dimList = data.item || [];
          this.total = data.total;
          this.leftLoading = false;
          if (!this.shows && this.cat) this.shows = this.cat.dimId;
        })
        .catch(err => {
          this.leftLoading = false;
          this.$message.error(err);
        });
    },
    // 接口详情（portal/api/interface/details?id=..），字段名与读取方式与原实现一致
    getDetail(id) {
      this.selectId = id;
      portalApi.getInterfaceDetail({ id: id })
        .then((res) => {
          // 空值守卫：接口未收录 / 返回 data 为 null 时，页面仍按「接口详情」兜底渲染，
          // 避免模板读取 detailObj.name 抛 TypeError（对齐 handoff 的 api 空值守卫）
          this.detailObj = (res && res.data) || {};
          // 切换接口时先清空上一份文档数据，避免解析失败后残留旧内容
          this.tableData = [];
          this.quaryData = [];
          this.respExample = undefined;
          this.codeData = [];
          try {
            this.tableData = JSON.parse(this.detailObj.reqParamPretty);
            const arr = JSON.parse(this.detailObj.respFiledDesc);
            arr.forEach((item) => {
              fillChildren(item);
            });
            this.quaryData = arr;
            this.respExample = JSON.parse(this.detailObj.respExample);
          } catch (e) {
            // 字段为空或非法 JSON 时保持空态（原实现同样静默处理）
          }
          this.codeData = this.detailObj.respCodes || [];
        })
        .catch(err => {
          this.$message.error(err);
        });
    }
  }
};
</script>

<style lang="less" scoped>
/* 页面级微调；handoff 的接口详情样式集中在 src/styles/site.css，
   门禁（.doc-gate）与返回字段缩进等本地适配见 src/styles/app.css */
.doc-card .api-doc {
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/api/apiDetail.vue